import {
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material';
import React from 'react';

import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import { ContextStore } from '../store/contextStore';

/**
 * "New campaign" modal that offers two paths (matching the Figma design):
 *   - Create new: brings the user to the blank editor.
 *   - Start from existing: pick an existing campaign to clone first.
 *
 * The modal doesn't perform the clone itself — it calls back to the parent
 * with either `{ mode: 'new' }` or `{ mode: 'existing', sourceId }`. The
 * parent wires that into navigation + the duplicate API.
 */
const NewCampaignDialog = ({ open, onClose, onSelect, existingCampaigns }) => {
    const { currentLicensee } = React.useContext(ContextStore);
    const [mode, setMode] = React.useState('new');
    const [source, setSource] = React.useState(null);
    const [tourOperator, setTourOperator] = React.useState(currentLicensee ?? null);

    const onLicenseeChange = (id) => {
        setTourOperator(id);
    };

    React.useEffect(() => {
        if (open) {
            setMode('new');
            setSource(null);
            setTourOperator(currentLicensee ?? null);
        }
    }, [open, currentLicensee]);

    const canSubmit = mode === 'new' || (mode === 'existing' && source && source.id != null);

    const handleSubmit = () => {
        if (!canSubmit) return;
        if (mode === 'new') {
            onSelect({ mode: 'new', tour_operator: tourOperator });
        } else {
            onSelect({ mode: 'existing', sourceId: source.id, sourceName: source.name, tour_operator: tourOperator });
        }
    };

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(tourOperator)}>
        <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogContent>
                <LicenseeFormField value={tourOperator} onChange={onLicenseeChange} />
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                    <FormControlLabel value="new" control={<Radio />} label="Create new" />
                    <FormControlLabel
                        value="existing"
                        control={<Radio />}
                        label="Start from existing campaign"
                        disabled={!existingCampaigns || existingCampaigns.length === 0}
                    />
                </RadioGroup>
                {mode === 'existing' && (
                    <Autocomplete
                        sx={{ mt: 1 }}
                        options={existingCampaigns || []}
                        getOptionLabel={(o) => o?.name || ''}
                        value={source}
                        onChange={(_, v) => setSource(v)}
                        renderInput={(params) => (
                            <TextField {...params} label="Campaign to clone" variant="filled" />
                        )}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <FormActionButtons
                    onSubmit={handleSubmit}
                    submitDisabled={!canSubmit}
                    submitLabel={mode === 'existing' ? 'Clone and open' : 'Create'}
                    onCancel={onClose}
                    size="medium"
                />
            </DialogActions>
        </Dialog>
        </FormLicenseeContext.Provider>
    );
};

export default NewCampaignDialog;
